import { APICall } from "@/config/NetworkUtility";
import { logApiError } from "@/services/apiErrorLogger";
import { createAsyncThunk } from "@reduxjs/toolkit";

const CACHE_TIME = 5 * 60 * 1000 // 5 min

/* ==========================================
   GET NAVIGATIONS
========================================== */

export const getNavigations = createAsyncThunk(
    "navigation/getNavigations",
    async (payload, { rejectWithValue, getState }) => {
        try {
            const {
                forceRefresh = false,
                ...queryPayload
            } = payload;

            const cacheKey = new URLSearchParams(
                queryPayload
            ).toString();

            const cachedData =
                getState()?.navigations?.cache?.[cacheKey];

            const isCacheValid =
                cachedData &&
                Date.now() - cachedData.timestamp <
                CACHE_TIME;

            if (
                isCacheValid &&
                !forceRefresh
            ) {
                return {
                    data: cachedData.data,
                    pagination:
                        cachedData.pagination,

                    cacheKey,

                    fromCache: true,
                };
            }

            const queryParams = new URLSearchParams({
                ...payload,
            }).toString();

            const url = `v1/NavigationSetup/GetNavigationList?${queryParams}`;

            const result = await APICall("get", payload, url);

            if (result?.statuscode === 200 || result?.statuscode === 201) {
                return {
                    data: result?.data || [],

                    pagination:
                        result?.pagination || {},

                    cacheKey,

                    fromCache: false,
                };
            }

            return rejectWithValue(result);
        } catch (error) {
            logApiError(error, {
                source: "navigations",
                endpoint: "getNavigations",
                payload,
            });

            return rejectWithValue(error);
        }
    },
);

/* ==========================================
   CREATE NAVIGATION
========================================== */

export const createNavigation = createAsyncThunk(
    "navigation/createNavigation",
    async (payload, { rejectWithValue }) => {
        try {
            const result = await APICall(
                "post",
                payload,
                "v1/NavigationSetup/NavigationInsert",
            );

            if (result?.statuscode === 200 || result?.statuscode === 201) {
                return result;
            }

            // API returned error payload but no exception
            return rejectWithValue(result);
        } catch (error) {
            logApiError(error, {
                source: "navigations",
                endpoint: "createNavigation",
                payload,
            });

            const errorData = error?.response?.data;
            // ASP.NET validation errors
            if (errorData?.errors) {
                return rejectWithValue(errorData);
            }

            return rejectWithValue({
                message:
                    errorData?.message || error?.message || "Failed to create navigation",
            });
        }
    },
);

/* ==========================================
   UPDATE NAVIGATION
========================================== */

export const updateNavigation = createAsyncThunk(
    "navigation/updateNavigation",
    async (payload, { rejectWithValue }) => {
        try {
            const result = await APICall(
                "post",
                payload,
                "v1/NavigationSetup/NavigationUpdate",
            );

            if (result?.statuscode === 200 || result?.statuscode === 201) {
                return result;
            }

            return rejectWithValue(result);
        } catch (error) {
            logApiError(error, {
                source: "navigations",
                endpoint: "updateNavigation",
                payload,
            });

            return rejectWithValue({
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Failed to update navigation",
            });
        }
    },
);

/* ==========================================
    NAVIGATION DROPDOWN LIST
========================================== */

const DROPDOWN_CACHE_TIME = 5 * 60 * 1000; // 5 minutes

export const getNavigationDropdownList = createAsyncThunk(
    "navigation/getNavigationDropdownList",

    async (
        payload = {},
        { getState, rejectWithValue }
    ) => {
        try {
            const {
                forceRefresh = false,
                ...queryPayload
            } = payload;

            const cacheKey = new URLSearchParams(
                Object.entries(queryPayload)
                    .filter(
                        ([, value]) =>
                            value !== undefined &&
                            value !== null
                    )
                    .sort(([keyA], [keyB]) =>
                        keyA.localeCompare(keyB)
                    )
            ).toString();

            const cachedData =
                getState()
                    ?.navigations
                    ?.dropdownCache?.[cacheKey];

            const isCacheValid =
                cachedData &&
                Date.now() -
                cachedData.timestamp <
                DROPDOWN_CACHE_TIME;


            if (
                isCacheValid &&
                !forceRefresh
            ) {
                return {
                    data: cachedData.data,

                    cacheKey,

                    fromCache: true,
                };
            }

            const queryParams =
                new URLSearchParams(
                    Object.entries(queryPayload)
                        .filter(
                            ([, value]) =>
                                value !== undefined &&
                                value !== null
                        )
                ).toString();

            const url =
                `v1/NavigationSetup/GetNavigationDropdownList?${queryParams}`;

            /*
            ==========================================
            API REQUEST
            ==========================================
            */

            const result = await APICall(
                "get",
                queryPayload,
                url
            );

            /*
            ==========================================
            API SUCCESS
            ==========================================
            */

            if (
                result?.statuscode === 200 ||
                result?.statuscode === 201
            ) {
                return {
                    data:
                        result?.data
                            ?.navigationList || [],

                    cacheKey,

                    fromCache: false,
                };
            }

            return rejectWithValue(result);

        } catch (error) {
            logApiError(error, {
                source: "navigation",
                endpoint:
                    "getNavigationDropdownList",
                payload,
            });

            return rejectWithValue(error);
        }
    }
);

/* ==========================================
    DELETE NAVIGATION 
========================================== */

export const deleteNavigation = createAsyncThunk(
    "navigation/deleteNavigation",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await APICall(
                "post",
                payload,
                "v1/NavigationSetup/DeleteNavigation",
            );

            if (![200, 201].includes(response?.statuscode)) {
                return rejectWithValue({
                    status: response?.statuscode,
                    message: response?.message ?? "Failed to delete navigation.",
                    data: response?.data ?? null,
                });
            }

            return response;
        } catch (error) {
            return rejectWithValue(getErrorPayload(error));
        }
    },
);
